import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import { 
  ArrowLeft, User, Mail, Phone, Edit, Briefcase, Link as LinkIcon,
  FileText, Clock, Plus, X, Save, MapPin, ChevronDown, ChevronUp,
  Calendar, Users, MessageSquare, Zap, PhoneCall, Upload, Trash2,
  CheckCircle, AlertCircle
} from 'lucide-react';
import axios from 'axios';
import CallHistoryTimeline from '../components/CallHistoryTimeline';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactDetailEnhanced = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Section expansion states
  const [expandedSections, setExpandedSections] = useState({
    basicInfo: true,
    workHistory: false,
    clientActivity: false,
    meetings: false,
    additionalContacts: false,
    worksiteAddresses: false,
    callQueue: false,
    automations: false,
    files: false
  });

  // Data states
  const [linkedJobs, setLinkedJobs] = useState([]);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [additionalContacts, setAdditionalContacts] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [files, setFiles] = useState([]);

  // Modal states
  const [isEditing, setIsEditing] = useState(false);
  const [activeModal, setActiveModal] = useState(null);

  // Form states
  const [editForm, setEditForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    relationship: '',
    notes: ''
  });

  const [activityForm, setActivityForm] = useState({
    activity_type: 'note',
    description: ''
  });

  const [meetingForm, setMeetingForm] = useState({
    title: '',
    type: 'meeting',
    scheduled_date: '',
    notes: ''
  });

  const [subContactForm, setSubContactForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    relationship: ''
  });

  const [addressForm, setAddressForm] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: ''
  });

  useEffect(() => {
    if (contactId) {
      fetchContactDetails();
      fetchLinkedJobs();
      fetchActivities();
      fetchMeetings();
      fetchAdditionalContacts();
      fetchAddresses();
    }
  }, [contactId]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const fetchContactDetails = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}`);
      setContact(response.data);
      setEditForm({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        relationship: response.data.relationship || '',
        notes: response.data.notes || ''
      });
    } catch (error) {
      console.error('Error fetching contact:', error);
      toast.error('Failed to load contact details');
      navigate('/contacts-new');
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedJobs = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/jobs`);
      setLinkedJobs(response.data.links || []);
    } catch (error) {
      console.error('Error fetching linked jobs:', error);
    }
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/activities`);
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  // Fetch functions for new sections
  const fetchMeetings = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/meetings`);
      setMeetings(response.data.meetings || []);
    } catch (error) {
      console.error('Error fetching meetings:', error);
    }
  };

  const fetchAdditionalContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/subcontacts`);
      setAdditionalContacts(response.data.subcontacts || []);
    } catch (error) {
      console.error('Error fetching subcontacts:', error);
    }
  };

  const fetchAddresses = async () => {
    try {
      const response = await axios.get(`${API}/contacts/${contactId}/addresses`);
      setAddresses(response.data.addresses || []);
    } catch (error) {
      console.error('Error fetching addresses:', error);
    }
  };

  const handleUpdateContact = async () => {
    try {
      await axios.put(`${API}/contacts/${contactId}`, editForm);
      toast.success('Contact updated successfully');
      setIsEditing(false);
      fetchContactDetails();
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error(error.response?.data?.detail || 'Failed to update contact');
    }
  };

  const handleLogActivity = async (e) => {
    e.preventDefault();
    if (!activityForm.description) {
      toast.error('Please enter a description');
      return;
    }

    try {
      await axios.post(`${API}/contacts/${contactId}/activities`, activityForm);
      toast.success('Activity logged successfully');
      setActiveModal(null);
      setActivityForm({ activity_type: 'note', description: '' });
      fetchActivities();
    } catch (error) {
      console.error('Error logging activity:', error);
      toast.error('Failed to log activity');
    }
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    if (!meetingForm.title || !meetingForm.scheduled_date) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      await axios.post(`${API}/contacts/${contactId}/meetings`, meetingForm);
      toast.success('Meeting created successfully');
      setActiveModal(null);
      setMeetingForm({ title: '', type: 'meeting', scheduled_date: '', notes: '' });
      fetchMeetings();
      fetchActivities();
    } catch (error) {
      console.error('Error creating meeting:', error);
      toast.error('Failed to create meeting');
    }
  };

  const handleCreateSubContact = async (e) => {
    e.preventDefault();
    if (!subContactForm.first_name || !subContactForm.last_name) {
      toast.error('First name and last name are required');
      return;
    }

    try {
      await axios.post(`${API}/contacts/${contactId}/subcontacts`, subContactForm);
      toast.success('Additional contact added successfully');
      setActiveModal(null);
      setSubContactForm({ first_name: '', last_name: '', email: '', phone: '', relationship: '' });
      fetchAdditionalContacts();
      fetchActivities();
    } catch (error) {
      console.error('Error creating sub-contact:', error);
      toast.error('Failed to add contact');
    }
  };

  const handleCreateAddress = async (e) => {
    e.preventDefault();
    if (!addressForm.name || !addressForm.address) {
      toast.error('Name and address are required');
      return;
    }

    try {
      await axios.post(`${API}/contacts/${contactId}/addresses`, addressForm);
      toast.success('Address added successfully');
      setActiveModal(null);
      setAddressForm({ name: '', address: '', city: '', state: '', zip: '' });
      fetchAddresses();
      fetchActivities();
    } catch (error) {
      console.error('Error creating address:', error);
      toast.error('Failed to add address');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading Contact...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (!contact) {
    return (
      <Layout>
        <div className="p-6">
          <p className="text-muted-foreground">Contact not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/contacts-new')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Contacts
          </Button>
          <Button
            onClick={() => setIsEditing(!isEditing)}
            variant={isEditing ? "outline" : "default"}
          >
            {isEditing ? (
              <>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </>
            ) : (
              <>
                <Edit className="h-4 w-4 mr-2" />
                Edit Contact
              </>
            )}
          </Button>
        </div>

        {/* Contact Name Card with Blue Header */}
        <Card className="bg-gradient-to-r from-blue-600 to-blue-700 text-white border-0">
          <CardHeader>
            <div className="flex items-center gap-3">
              <User className="h-8 w-8" />
              <div>
                <CardTitle className="text-2xl text-white">
                  {contact.first_name} {contact.last_name}
                </CardTitle>
                {contact.relationship && (
                  <Badge className="mt-2 bg-white/20 text-white border-0">
                    {contact.relationship}
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Basic Info - Collapsible Card */}
        <Card>
          <Collapsible open={expandedSections.basicInfo} onOpenChange={() => toggleSection('basicInfo')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-primary" />
                    <CardTitle>Contact Information</CardTitle>
                  </div>
                  {expandedSections.basicInfo ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                {isEditing ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>First Name *</Label>
                        <Input
                          value={editForm.first_name}
                          onChange={(e) => setEditForm({...editForm, first_name: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Last Name *</Label>
                        <Input
                          value={editForm.last_name}
                          onChange={(e) => setEditForm({...editForm, last_name: e.target.value})}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={editForm.email}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>Phone</Label>
                        <Input
                          value={editForm.phone}
                          onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Relationship</Label>
                      <Select
                        value={editForm.relationship}
                        onValueChange={(value) => setEditForm({...editForm, relationship: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Homeowner">Homeowner</SelectItem>
                          <SelectItem value="Property Manager">Property Manager</SelectItem>
                          <SelectItem value="Tenant">Tenant</SelectItem>
                          <SelectItem value="Contractor">Contractor</SelectItem>
                          <SelectItem value="Real Estate Agent">Real Estate Agent</SelectItem>
                          <SelectItem value="Insurance Agent">Insurance Agent</SelectItem>
                          <SelectItem value="Referral Partner">Referral Partner</SelectItem>
                          <SelectItem value="Vendor">Vendor</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        value={editForm.notes}
                        onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                        rows={4}
                      />
                    </div>
                    <Button onClick={handleUpdateContact} className="bg-green-600 hover:bg-green-700">
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <div>
                          <Label className="text-muted-foreground">Email</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="h-4 w-4 text-muted-foreground" />
                            <p className="text-base">{contact.email || 'N/A'}</p>
                          </div>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Phone</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Phone className="h-4 w-4 text-muted-foreground" />
                            <p className="text-base">{contact.phone || 'N/A'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-3">
                        <div>
                          <Label className="text-muted-foreground">Relationship</Label>
                          <p className="text-base mt-1">{contact.relationship || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground">Created</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <p className="text-base">
                              {new Date(contact.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    {contact.notes && (
                      <div className="mt-4 pt-4 border-t">
                        <Label className="text-muted-foreground">Notes</Label>
                        <p className="text-base mt-2 whitespace-pre-wrap">{contact.notes}</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Work History (Linked Jobs) - Collapsible Card */}
        <Card>
          <Collapsible open={expandedSections.workHistory} onOpenChange={() => toggleSection('workHistory')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Work History</CardTitle>
                      <CardDescription>Projects and jobs associated with this contact</CardDescription>
                    </div>
                  </div>
                  {expandedSections.workHistory ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" />
                    New Project
                  </Button>
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" />
                    New Work Order
                  </Button>
                </div>
                {linkedJobs.length === 0 ? (
                  <div className="text-center py-12">
                    <Briefcase className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No work history yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Update</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {linkedJobs.map((link) => (
                        <TableRow key={link.id}>
                          <TableCell>{link.role}</TableCell>
                          <TableCell>{link.job?.job_name || link.job?.job_number}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{link.job?.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {link.job?.updated_at ? new Date(link.job.updated_at).toLocaleDateString() : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Client Activity - Collapsible Card */}
        <Card>
          <Collapsible open={expandedSections.clientActivity} onOpenChange={() => toggleSection('clientActivity')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Client Activity</CardTitle>
                      <CardDescription>Timeline of interactions and updates</CardDescription>
                    </div>
                  </div>
                  {expandedSections.clientActivity ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Button className="bg-green-600 hover:bg-green-700" size="sm">
                    <Mail className="h-4 w-4 mr-2" />
                    Email
                  </Button>
                  <Button className="bg-green-600 hover:bg-green-700" size="sm">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    SMS
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700" 
                    size="sm"
                    onClick={() => setActiveModal('activity')}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    New Note
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700" 
                    size="sm"
                    onClick={() => setActiveModal('activity')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Log Activity
                  </Button>
                  <Button className="bg-green-600 hover:bg-green-700" size="sm">
                    <CheckCircle className="h-4 w-4 mr-2" />
                    New To-do
                  </Button>
                </div>
                {activities.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No activity yet</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Description</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {activities.map((activity) => (
                        <TableRow key={activity.id}>
                          <TableCell>
                            <Badge variant="outline">{activity.activity_type}</Badge>
                          </TableCell>
                          <TableCell>{new Date(activity.created_at).toLocaleString()}</TableCell>
                          <TableCell>{activity.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Meetings & Reminders - Collapsible Card */}
        <Card>
          <Collapsible open={expandedSections.meetings} onOpenChange={() => toggleSection('meetings')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Meetings & Reminders</CardTitle>
                      <CardDescription>Scheduled meetings and reminders</CardDescription>
                    </div>
                  </div>
                  {expandedSections.meetings ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="flex gap-2 mb-4">
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Zap className="h-4 w-4 mr-2" />
                    Quick Scheduler
                  </Button>
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => setActiveModal('meeting')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    New Meeting
                  </Button>
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" />
                    New Reminder
                  </Button>
                </div>
                {meetings.length === 0 ? (
                  <div className="text-center py-12">
                    <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No meetings or reminders scheduled</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Assigned To</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {meetings.map((meeting) => (
                        <TableRow key={meeting.id}>
                          <TableCell>{meeting.assigned_to}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{meeting.type}</Badge>
                          </TableCell>
                          <TableCell>{meeting.title}</TableCell>
                          <TableCell>
                            {new Date(meeting.scheduled_date).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm('Delete this meeting?')) {
                                  try {
                                    await axios.delete(`${API}/contacts/${contactId}/meetings/${meeting.id}`);
                                    toast.success('Meeting deleted');
                                    fetchMeetings();
                                  } catch (error) {
                                    toast.error('Failed to delete meeting');
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Additional Contacts - Collapsible Card */}
        <Card>
          <Collapsible open={expandedSections.additionalContacts} onOpenChange={() => toggleSection('additionalContacts')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Additional Contacts</CardTitle>
                      <CardDescription>Related contacts and sub-contacts</CardDescription>
                    </div>
                  </div>
                  {expandedSections.additionalContacts ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <Button 
                  className="bg-green-600 hover:bg-green-700 mb-4"
                  onClick={() => setActiveModal('subContact')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Contact
                </Button>
                {additionalContacts.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No additional contacts</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Relationship</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {additionalContacts.map((subContact) => (
                        <TableRow key={subContact.id}>
                          <TableCell>
                            {subContact.first_name} {subContact.last_name}
                          </TableCell>
                          <TableCell>{subContact.email || '-'}</TableCell>
                          <TableCell>{subContact.phone || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{subContact.relationship || '-'}</Badge>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm('Delete this contact?')) {
                                  try {
                                    await axios.delete(`${API}/contacts/${contactId}/subcontacts/${subContact.id}`);
                                    toast.success('Contact deleted');
                                    fetchAdditionalContacts();
                                  } catch (error) {
                                    toast.error('Failed to delete contact');
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Worksite Addresses - Collapsible Card */}
        <Card>
          <Collapsible open={expandedSections.worksiteAddresses} onOpenChange={() => toggleSection('worksiteAddresses')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Worksite Addresses</CardTitle>
                      <CardDescription>Property and worksite locations</CardDescription>
                    </div>
                  </div>
                  {expandedSections.worksiteAddresses ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <Button 
                  className="bg-green-600 hover:bg-green-700 mb-4"
                  onClick={() => setActiveModal('address')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  New Address
                </Button>
                {addresses.length === 0 ? (
                  <div className="text-center py-12">
                    <MapPin className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground">No addresses added</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Name</TableHead>
                        <TableHead>Address</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {addresses.map((addr) => (
                        <TableRow key={addr.id}>
                          <TableCell className="font-medium">{addr.name}</TableCell>
                          <TableCell>
                            {addr.address}
                            {addr.city && `, ${addr.city}`}
                            {addr.state && `, ${addr.state}`}
                            {addr.zip && ` ${addr.zip}`}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={async () => {
                                if (window.confirm('Delete this address?')) {
                                  try {
                                    await axios.delete(`${API}/contacts/${contactId}/addresses/${addr.id}`);
                                    toast.success('Address deleted');
                                    fetchAddresses();
                                  } catch (error) {
                                    toast.error('Failed to delete address');
                                  }
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Call History Timeline - Safe Mode Read-Only */}
        <Card>
          <Collapsible open={expandedSections.callQueue} onOpenChange={() => toggleSection('callQueue')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <PhoneCall className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Call History</CardTitle>
                      <CardDescription>All calls with this contact from CallRail</CardDescription>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Safe Mode</Badge>
                    {expandedSections.callQueue ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0">
                <CallHistoryTimeline 
                  contactId={contactId}
                  limit={5}
                  showHeader={false}
                  compact={false}
                />
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Automations - Placeholder */}
        <Card>
          <Collapsible open={expandedSections.automations} onOpenChange={() => toggleSection('automations')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Automations</CardTitle>
                      <CardDescription>Automated workflows (Coming Soon)</CardDescription>
                    </div>
                  </div>
                  {expandedSections.automations ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="text-center py-12">
                  <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Feature coming soon</p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Files - Collapsible Card */}
        <Card>
          <Collapsible open={expandedSections.files} onOpenChange={() => toggleSection('files')}>
            <CollapsibleTrigger asChild>
              <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-primary" />
                    <div>
                      <CardTitle>Files</CardTitle>
                      <CardDescription>Documents and attachments</CardDescription>
                    </div>
                  </div>
                  {expandedSections.files ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                </div>
              </CardHeader>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent>
                <div className="border-2 border-dashed rounded-lg p-8 text-center mb-4">
                  <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Click here or drop file(s) here to upload</p>
                </div>
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No files uploaded yet</p>
                </div>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
        </Card>

        {/* Activity Modal */}
        <Dialog open={activeModal === 'activity'} onOpenChange={() => setActiveModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Activity</DialogTitle>
              <DialogDescription>
                Record an interaction or note for this contact
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleLogActivity} className="space-y-4">
              <div>
                <Label>Activity Type</Label>
                <Select
                  value={activityForm.activity_type}
                  onValueChange={(value) => setActivityForm({...activityForm, activity_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="note">Note</SelectItem>
                    <SelectItem value="call">Phone Call</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="meeting">Meeting</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={activityForm.description}
                  onChange={(e) => setActivityForm({...activityForm, description: e.target.value})}
                  rows={4}
                  placeholder="Enter activity details..."
                  required
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setActiveModal(null)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Log Activity
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Meeting Modal */}
        <Dialog open={activeModal === 'meeting'} onOpenChange={() => setActiveModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New Meeting</DialogTitle>
              <DialogDescription>Schedule a meeting with this contact</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateMeeting} className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={meetingForm.title}
                  onChange={(e) => setMeetingForm({...meetingForm, title: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Date & Time</Label>
                <Input
                  type="datetime-local"
                  value={meetingForm.scheduled_date}
                  onChange={(e) => setMeetingForm({...meetingForm, scheduled_date: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={meetingForm.notes}
                  onChange={(e) => setMeetingForm({...meetingForm, notes: e.target.value})}
                  rows={3}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setActiveModal(null)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Create Meeting
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Sub-Contact Modal */}
        <Dialog open={activeModal === 'subContact'} onOpenChange={() => setActiveModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Additional Contact</DialogTitle>
              <DialogDescription>Add a related contact</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateSubContact} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>First Name</Label>
                  <Input
                    value={subContactForm.first_name}
                    onChange={(e) => setSubContactForm({...subContactForm, first_name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label>Last Name</Label>
                  <Input
                    value={subContactForm.last_name}
                    onChange={(e) => setSubContactForm({...subContactForm, last_name: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={subContactForm.email}
                  onChange={(e) => setSubContactForm({...subContactForm, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Phone</Label>
                <Input
                  value={subContactForm.phone}
                  onChange={(e) => setSubContactForm({...subContactForm, phone: e.target.value})}
                />
              </div>
              <div>
                <Label>Relationship</Label>
                <Input
                  value={subContactForm.relationship}
                  onChange={(e) => setSubContactForm({...subContactForm, relationship: e.target.value})}
                  placeholder="e.g., Spouse, Assistant"
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setActiveModal(null)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Add Contact
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Address Modal */}
        <Dialog open={activeModal === 'address'} onOpenChange={() => setActiveModal(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Worksite Address</DialogTitle>
              <DialogDescription>Add a property or worksite location</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateAddress} className="space-y-4">
              <div>
                <Label>Name/Label</Label>
                <Input
                  value={addressForm.name}
                  onChange={(e) => setAddressForm({...addressForm, name: e.target.value})}
                  placeholder="e.g., Main Residence, Rental Property"
                  required
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={addressForm.address}
                  onChange={(e) => setAddressForm({...addressForm, address: e.target.value})}
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>City</Label>
                  <Input
                    value={addressForm.city}
                    onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={addressForm.state}
                    onChange={(e) => setAddressForm({...addressForm, state: e.target.value})}
                  />
                </div>
                <div>
                  <Label>ZIP</Label>
                  <Input
                    value={addressForm.zip}
                    onChange={(e) => setAddressForm({...addressForm, zip: e.target.value})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setActiveModal(null)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  Add Address
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ContactDetailEnhanced;
